<template>
  <div class="homepage">
    <!-- Customer Intro new -->
    <div class="customer-intro-new homepage__customer-intro-new">
      <div class="customer-intro-new__left-side">
        <div class="customer-intro-new__title">
          End-to-End <br />
          <span class="customer-intro-new__title-keyword">UGC</span> Solutions <br />
          for Every Destination
        </div>
        <div class="customer-intro-new__subtitle">From content strategy to creation and distribution, Shrpa helps destination marketing teams of all sizes tell authentic stories at scale.</div>
        <SrpButton class="customer-intro-new__cta-button" color="orange" @click="$refs.domRefRequestADemoFormModal?.openModal" style="box-shadow: 0 8px 30px -5px #ec563b">
          Book a Demo
          <template #iconRight><IconEmbedded name="caret-right_4" :size="18" /></template>
        </SrpButton>
      </div>

      <div class="customer-intro-new__right-side">
        <CardsCarousel class="customer-intro-new__photos-carousel" />
      </div>
    </div>
    <!-- / Customer Intro new -->

    <div class="hero white home-section no-top-margin pale-teal-bg">
      <div class="ui segment blank" style="background: transparent">
        <div class="ui container">
          <div class="ui three column relaxed stackable grid">
            <div class="column promo">
              <div class="promo-wrapper">
                <div class="icon" style="width: 35px; min-width: 35px; max-width: 35px; height: 35px; display: flex; justify-content: center; align-items: center">
                  <IconEmbedded name="ai-triple-solid" :size="49" color="rgba(16, 134, 136, 1)" />
                </div>
                <div>
                  <h3 class="ui header promo-header">
                    <div class="content" style="margin-bottom: 5px">Insights &amp; Strategies That Deliver</div>
                    <div class="sub header differentiator-text">
                      Leverage insights initiated by you, augmented by hundreds of similar destinations, and optimized through AI to identify content gaps and guide strategy.
                    </div>
                  </h3>
                </div>
              </div>
            </div>
            <div class="column promo">
              <div class="promo-wrapper">
                <div class="icon" style="width: 35px; min-width: 35px; max-width: 35px; height: 35px; display: flex; justify-content: center; align-items: center">
                  <IconEmbedded name="camera-solid" :size="40" color="rgba(16, 134, 136, 1)" />
                </div>
                <div>
                  <h3 class="ui header promo-header">
                    <div class="content" style="margin-bottom: 5px">Industry Leading Collaboration</div>
                    <div class="sub header differentiator-text">
                      Trust the industry leader in creator collaborations and the most engaged and diverse pool of creators. Enable streamlined collaboration with everyone from locals to national
                      influencers.
                    </div>
                  </h3>
                </div>
              </div>
            </div>
            <div class="column promo">
              <div class="promo-wrapper">
                <div class="icon" style="width: 35px; min-width: 35px; max-width: 35px; height: 35px; display: flex; justify-content: center; align-items: center">
                  <IconEmbedded name="share-up_2" :size="38" color="rgba(16, 134, 136, 1)" />
                </div>
                <div>
                  <h3 class="ui header promo-header">
                    <div class="content" style="margin-bottom: 5px">Hassle Free Content Rights</div>
                    <div class="sub header differentiator-text">
                      Hassle free content rights enable single click website enhancements & a weekly feed of social posts to ensure that UGC isn’t just effortlessly collected but also shared with
                      ease.
                    </div>
                  </h3>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Loader v-if="isLoading" class="recent-creator-visits__loader" />

    <div v-else style="position: relative; overflow: hidden">
      <div v-if="showCustomerQuotes">
        <Loader v-if="isLoadingCustomerQuotesAndLogos" class="recent-creator-visits__loader" />

        <div v-else class="logos-and-testimonials-carousels" style="margin: 0 auto">
          <h1 class="global-h1 logos-and-testimonials-carousels__title" style="color: #102f41; font-weight: 600">
            Trusted by <span style="color: #058587">hundreds</span> of destinations large and small
          </h1>

          <HorizontalStepCarousel
            class="logos-and-testimonials-carousels__logos-carousel"
            itemWrapClassName="logos-row__logo-wrap"
            itemClassName="logos-row__logo"
            :stepSize="
              {
                'desktop-wide': 3,
                desktop: 3,
                laptop: 3,
                'tablet-large': 2,
                tablet: 2,
                mobile: 2,
              }[screenSize]
            "
            :transitionDuration="200"
          >
            <ul class="logos-row" style="width: 100%">
              <li class="logos-row__logo-wrap" v-for="(logoFileName, index) in customerLogos" :key="logoFileName">
                <img class="logos-row__logo" :src="`${contentBaseUri}/cms/images/orgs/${logoFileName}`" :alt="logoFileName" />
              </li>
            </ul>
          </HorizontalStepCarousel>

          <div class="logos-and-testimonials-carousels__divider"></div>

          <HorizontalStepCarousel
            class="logos-and-testimonials-carousels__quotes-carousel"
            itemWrapClassName="testimonials-row__item-wrap"
            itemClassName="testimonials-row__item"
            :stepSize="
              {
                'desktop-wide': 3,
                desktop: 3,
                laptop: 3,
                'tablet-large': 2,
                tablet: 1,
                mobile: 1,
              }[screenSize]
            "
            :transitionDuration="350"
          >
            <div class="testimonials-row" style="width: 100%">
              <ul class="testimonials-row__row-itself">
                <li class="testimonials-row__item-wrap" v-for="quote in customerQuotes" :key="quote.id">
                  <!-- Testimonial snippet -->
                  <div class="testimonial-snippet testimonials-row__item">
                    <div class="testimonial-snippet__logo-wrap">
                      <img v-if="quote.customerIcon" class="testimonial-snippet__logo" :src="`${contentBaseUri}/cms/images/orgs/${quote.customerIcon}`" :alt="quote.fullName" />
                    </div>

                    <div class="global-h5 testimonial-snippet__text">
                      <ClampedText :visibleLines="10">
                        <span v-html="formatCustomerQuoteText(quote.quoteText, quote.quoteAccentText)"></span>
                      </ClampedText>
                    </div>

                    <div class="testimonial-snippet__divider"></div>

                    <div class="testimonial-snippet__name-and-position">
                      <div class="global-h4 testimonial-snippet__name">
                        {{ quote.fullName }}
                      </div>
                      <div class="testimonial-snippet__position">
                        {{ quote.position }}
                      </div>
                    </div>
                  </div>
                  <!-- / Testimonial snippet -->
                </li>
              </ul>
            </div>
          </HorizontalStepCarousel>

          <!--<LinkWithIcon tag="router-link" class="logos-and-testimonials-carousels__see-all-link" :to="{ name: 'CustomerQuotes' }" iconInset="auto -5px auto auto" color="blue">-->
          <!--  <span>See more</span>-->
          <!--  <template #iconRight><IconEmbedded name="arrow-right_3" :size="17" /></template>-->
          <!--</LinkWithIcon>-->

          <SrpButton
            class="logos-and-testimonials-carousels__see-all-link"
            color="orange"
            tag="RouterLink"
            :to="{
              name: 'CustomerQuotes',
            }"
            style="box-shadow: 0 8px 30px -5px #ec563b"
          >
            Customers &amp; Case Studies
            <template #iconRight><IconEmbedded name="caret-right_4" :size="18" /></template>
          </SrpButton>

          <div class="logos-and-testimonials-carousels__divider"></div>
        </div>
      </div>

      <div class="hero home-section green-section" style="margin-bottom: 3em; background: white">
        <div class="ui container" style="width: var(--grid-content-full-width); padding-bottom: 45px; margin-bottom: 0; border-bottom: 1px rgba(0, 0, 0, 0.1) solid">
          <div class="creators-header-group" style="width: 100%; flex-direction: column; align-items: stretch">
            <div style="margin-top: 28px; padding-top: 10px">
              <h1 class="global-h1 ui header" style="width: 100%; color: #102f41; font-size: 35px; line-height: 38px; text-align: center">Recent Creator Visits</h1>
            </div>
          </div>
          <div style="margin-top: 10px">
            <p id="page-subtitle" style="color: rgba(0, 0, 0, 0.5); font-family: sans-serif; text-align: center">
              Explore some creator visits that generated authentic content for our partner destinations!
            </p>
          </div>
        </div>
      </div>

      <div class="recent-creator-visits__visit-summaries-list-wrap">
        <ul class="recent-creator-visits__visit-summaries-list">
          <template v-for="(summarySection, index) in summarySections" :key="`visit-summary-${index}`">
            <CollabSummarySection class="recent-creator-visits__summary-section" :summarySection="summarySection" />

            <div v-if="index !== summarySections.length - 1" style="margin-bottom: 70px; border-bottom: 1px rgba(0, 0, 0, 0.1) solid"></div>
          </template>
        </ul>

        <!-- Book a demo button section with halo -->
        <div class="book-a-demo-button-section-with-halo recent-creator-visits__book-a-demo-button-section-with-halo">
          <SrpButton class="book-a-demo-button-section-with-halo__button" color="orange" @click="$refs.domRefRequestADemoFormModal?.openModal">Book a Demo</SrpButton>

          <div class="book-a-demo-button-section-with-halo__halo"></div>
        </div>
        <!-- / Book a demo button section with halo -->
      </div>
    </div>

    <!-- Welcome travelers section -->
    <div class="welcome-travelers-section homepage__welcome-travelers-section">
      <ul class="welcome-travelers-section__avatars-row">
        <AvatarWithFallback
          class="welcome-travelers-section__avatar"
          v-for="(avatarUrl, index) in [
            'https://dev-cdn.shrpa.com/cms/images/profiles/47d7ee26-0e1f-46e6-ae3b-bea99cd45a01.jpg',
            'https://cdn.shrpa.com/cms/images/profiles/2543ba8d-5f3b-4c5f-8bce-d1ed65cec3b2.jpg',
            'https://dev-cdn.shrpa.com/cms/images/profiles/49b4002e-9982-44bd-bda2-158be5d5fbf8.jpg',
            'https://dev-cdn.shrpa.com/cms/images/profiles/c5c1d14a-9b81-4681-aad2-ea2f35a5a484.jpg',
            'https://cdn.shrpa.com/cms/images/profiles/e28e4bf1-2a3a-43b9-b752-cbb908ef9947.jpg',
          ]"
          :key="`avatar-${index}`"
          :src="avatarUrl"
        />
        <!--&lt;!&ndash; "How much more" circle &ndash;&gt;-->
        <!--<li class="how-much-more-circle welcome-travelers-section__how-much-more-circle">-->
        <!--  <div class="global-h3 how-much-more-circle__number">+1,024</div>-->
        <!--  <div class="how-much-more-circle__more-word">more</div>-->
        <!--</li>-->
        <!--&lt;!&ndash; / "How much more" circle &ndash;&gt;-->
      </ul>

      <div class="global-h2 welcome-travelers-section__title">
        Ready to welcome <span class="welcome-travelers-section__title-em-color">travelers</span> and allow them to see themselves in your destination’s story?
      </div>

      <SrpButton class="welcome-travelers-section__cta-button" color="orange" size="big" @click="$refs.domRefRequestADemoFormModal?.openModal" style="box-shadow: 0 8px 30px -5px #ec563b">
        Book a Demo
      </SrpButton>

      <RequestADemoFormModal ref="domRefRequestADemoFormModal" />
    </div>
    <!-- / Welcome travelers section -->
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import axios from "axios";
import { inject } from "vue";
import { useHead } from "@unhead/vue";

// Helpers
import { formatCustomerQuoteText } from "@/helpers/CustomerQuoteHelper";

// Repos
import CustomerQuotesRepo from "@/repos/CustomerQuotesRepo";

// Components
import SrpButton from "@components/ui/SrpButton.vue";
import Loader from "@components/Loader/Loader.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import CollabSummarySection from "@components/CollabSummarySection.vue";
import HorizontalStepCarousel from "@components/HorizontalStepCarousel.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import CardsCarousel from "./CardsCarousel.vue";
import ClampedText from "@components/ui/ClampedText.vue";
import AvatarWithFallback from "@components/AvatarWithFallback.vue";
import RequestADemoFormModal from "./RequestADemoFormModal.vue";
import LoginModal from "@components/Modals/LoginModal.vue";

// Types
import { CollabSummaryModel } from "@contracts/collabCredits";
import { ScreenSize } from "@contracts/screenSize";
import { CustomerQuote } from "@contracts/pages";

export default defineComponent({
  name: "HomeV5",

  components: {
    LoginModal,
    AvatarWithFallback,
    ClampedText,
    LinkWithIcon,
    HorizontalStepCarousel,
    CollabSummarySection,
    IconEmbedded,
    Loader,
    SrpButton,
    CardsCarousel,
    RequestADemoFormModal,
  },

  data() {
    return {
      globalLog: inject("globalLog") as any,
      globalGetLink: inject("globalGetLink") as any,

      screenSize: inject("screenSize") as ScreenSize,

      // @ts-ignore
      contentBaseUri: globalThis.Bootstrap.Config.contentCdnBaseUri,
      isLoading: true,

      summarySections: [] as Array<CollabSummaryModel>,

      logosRowPosition: 0,
      testimonialsRowPosition: 0,

      isLoadingCustomerQuotesAndLogos: true,
      showCustomerQuotes: true,
      customerLogos: [] as Array<string>,
      customerQuotes: [] as Array<CustomerQuote>,

      isLoginModalVisible: false,
    };
  },

  watch: {
    screenSize() {
      this.logosRowPosition = 0;
      this.testimonialsRowPosition = 0;
    },
  },

  async mounted() {
    useHead({ title: "Shrpa - Home" });

    console.info("HomeV5 Mounted");
    await this.loadAllData();
  },

  methods: {
    formatCustomerQuoteText,
    async loadAllData() {
      this.loadCustomerQuotes();
      this.loadRecentCreatorVisits();
    },
    async loadCustomerQuotes() {
      const data = await CustomerQuotesRepo.getCustomerQuotes({ logosCount: 30, quotesCount: 6 });

      this.customerLogos = data.logos;
      this.customerQuotes = data.customerQuotes;

      this.isLoadingCustomerQuotesAndLogos = false;
    },
    async loadRecentCreatorVisits() {
      const uri = `${import.meta.env.VITE_API_URL}/collabsummary/recent`;
      const response = await axios.get(uri);
      this.isLoading = false;

      this.summarySections = response.data;
    },
  },
});
</script>

<style scoped lang="scss">
@import "@/scss/variables.scss";
@import "@/scss/screen-size-ranges.scss";

// "How much more" circle =====================================================
.how-much-more-circle {
  width: 108px;
  height: 108px;
  padding-top: 5px;
  box-sizing: border-box;
  outline: 3px #e8edf1 solid;
  border-radius: 1000px;
  outline-offset: -2px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: rgba(255, 255, 255, 1);

  &__number {
    color: #102f41;
    line-height: 22px;
  }

  &__more-word {
    @include standardFont;
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .how-much-more-circle {
    width: 85px;
    height: 85px;
  }
}

// Welcome travelers section ==================================================
.welcome-travelers-section {
  padding: 90px 0 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #e8edf1;

  &__avatars-row {
    padding: 0;
    margin: 0 0 25px;
    list-style: none;
    display: flex;
  }

  &__avatar {
    width: 108px !important;
    height: 108px;
    margin-right: -21px;

    &:nth-child(1) {
      z-index: 5;
    }
    &:nth-child(2) {
      z-index: 4;
    }
    &:nth-child(3) {
      z-index: 3;
    }
    &:nth-child(4) {
      z-index: 2;
    }
    &:nth-child(5) {
      z-index: 1;
    }

    :deep(.avatar-with-fallback__img-wrap) {
      outline: 3px #e8edf1 solid;
      border-radius: 1000px;
      outline-offset: -2px;
    }
  }

  &__how-much-more-circle {
  }

  &__title {
    max-width: 580px;
    margin-bottom: 35px;
    color: #102f41;
    text-align: center;
  }

  &__title-em-color {
    color: #108689;
  }

  &__cta-button {
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .welcome-travelers-section {
    &__avatar {
      width: 108px !important;
      height: 108px;
      margin-right: -21px;

      &:nth-child(4) {
        display: none;
      }
      &:nth-child(5) {
        display: none;
      }
    }
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .welcome-travelers-section {
    &__avatar {
      width: 85px !important;
      height: 85px;
      margin-right: -21px;

      &:nth-child(4) {
        display: none;
      }
      &:nth-child(5) {
        display: none;
      }
    }
  }
}

// Customer Intro new =========================================================
.customer-intro-new {
  display: flex;
  align-items: center;

  &__left-side {
    width: 50%;
  }

  &__title {
    margin-bottom: 15px;
    color: #112f41;
    font:
      700 50px/60px "Quicksand",
      sans-serif;
  }

  &__title-keyword {
    color: #108689;
  }

  &__subtitle {
    @include standardFont;
    max-width: 450px;
    margin-bottom: 30px;
    font: 17px/23px sans-serif;
  }

  &__cta-button {
    box-shadow: 0 8px 30px -5px #ec563b;
  }

  &__right-side {
    width: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__photos-carousel {
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
  .customer-intro-new {
    height: auto;
    flex-direction: column;

    &__left-side {
      width: 100%;
      margin-bottom: 80px;
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    &__title {
      text-align: center;
      text-wrap: balance;

      :deep(br) {
        display: none;
      }
    }

    &__subtitle {
      max-width: unset;
      text-align: center;
      text-wrap: balance;
    }

    &__cta-button {
    }

    &__right-side {
      width: 100dvw;
      position: relative;
      overflow: hidden;
    }
  }
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .customer-intro-new {
    height: auto;
    flex-direction: column;

    &__left-side {
      width: 100%;
      margin-bottom: 80px;
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    &__title {
      text-align: center;
      text-wrap: balance;

      :deep(br) {
        display: none;
      }
    }

    &__subtitle {
      max-width: unset;
      text-align: center;
      text-wrap: balance;
    }

    &__cta-button {
    }

    &__right-side {
      width: 100dvw;
      position: relative;
      overflow: hidden;
    }
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .customer-intro-new {
    height: auto;
    flex-direction: column;

    &__left-side {
      width: 100%;
      margin-bottom: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    &__title {
      font-size: 40px;
      line-height: 44px;
      text-align: center;
      text-wrap: balance;

      :deep(br) {
        display: none;
      }
    }

    &__subtitle {
      max-width: unset;
      text-align: center;
      text-wrap: balance;
    }

    &__cta-button {
    }

    &__right-side {
      width: 100dvw;
      position: relative;
      overflow: hidden;
    }
  }
}

// Homepage ===================================================================
.homepage {
  padding-top: 100px;
  display: flex;
  flex-direction: column;
  align-items: stretch;

  &__main-header {
    margin: 0 auto 110px;
  }

  &__customer-intro-new {
    width: calc($laptop-content-width + 20px);
    margin: 0 auto 130px;
  }

  &__welcome-travelers-section {
    // margin-bottom: 155px;
    position: relative;
    z-index: 4;
  }

  &__footer-global {
    margin: 0 auto 80px;
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
  .homepage {
    &__customer-intro-new {
      width: calc($laptop-content-width + 20px);
    }
  }
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
  .homepage {
    &__customer-intro-new {
      width: calc($laptop-content-width + 20px);
    }
  }
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
  .homepage {
    &__main-header {
      margin-bottom: 80px;
    }

    &__customer-intro-new {
      width: $tablet-large-content-width;
    }

    &__welcome-travelers-section {
      // margin-bottom: 115px;
    }

    &__footer-global {
      margin: 0 auto 40px;
    }
  }
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .homepage {
    &__main-header {
      margin-bottom: 80px;
    }

    &__customer-intro-new {
      width: $tablet-content-width;
    }

    &__welcome-travelers-section {
      // margin-bottom: 115px;
    }

    &__footer-global {
      margin: 0 auto 40px;
    }
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .homepage {
    &__main-header {
      margin-bottom: 55px;
    }

    &__customer-intro-new {
      width: $mobile-content-width;
      margin-bottom: 40px;
    }

    &__welcome-travelers-section {
      // margin-bottom: 90px;
    }

    &__footer-global {
      margin: 0 auto 40px;
    }
  }
}

// Logos row ==================================================================
.logos-row {
  padding: 0;
  margin: 0;
  display: flex;
  list-style: none;

  &__logo-wrap {
    height: 95px;
    padding-right: 71px;
    display: flex;
    justify-content: center;
    align-items: center;

    &:last-child {
      padding-right: 0;
    }
  }

  &__logo {
    max-width: 190px;
    max-height: 95px;
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .logos-row {
    &__logo-wrap {
      height: 65px;
      padding-right: 45px;
    }

    &__logo {
      max-width: 130px;
      max-height: 65px;
    }
  }
}

// Testimonial snippet ========================================================
.testimonial-snippet {
  @include standardFont;
  box-sizing: border-box;
  padding: 25px 25px 25px;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.37);
  background: 25px 19px/42px 28px url(/images/quote-icon.svg) no-repeat;

  &__logo-wrap {
    width: 200px;
    height: 80px;
    display: flex;
    align-self: flex-end;
    justify-content: flex-end;
    align-items: center;
  }

  &__logo {
    width: 100%;
    height: 100%;
    margin-bottom: 17px;
    object-fit: contain;
    object-position: right;
  }

  &__text {
    margin-bottom: 18px;
    color: rgba(91, 91, 91, 1);
    font-weight: 500;
    line-height: 22px;
    white-space: pre-wrap;

    :deep(b) {
      color: rgba(0, 0, 0, 1) !important;
    }
  }

  &__divider {
    width: 200px;
    margin-bottom: 17px;
    border-bottom: 1px rgba(0, 0, 0, 0.1) solid;
  }

  &__name-and-position {
  }

  &__name {
  }

  &__position {
  }
}

// Testimonials row ===========================================================
.testimonials-row {
  padding: 30px 0 50px;

  &__row-itself {
    width: calc($desktop-wide-grid-col-width * 4 + $desktop-wide-grid-gap-width * 3 + $desktop-wide-grid-gap-width);
    min-width: calc($desktop-wide-grid-col-width * 4 + $desktop-wide-grid-gap-width * 3 + $desktop-wide-grid-gap-width);
    padding: 0;
    margin: 0;
    display: flex;
    align-items: flex-start;
    list-style: none;
    transition: transform 0.15s ease-in-out;
  }

  &__item-wrap {
    width: 100%;
    min-width: 100%;
    padding-right: $desktop-wide-grid-gap-width;

    &:last-child {
      width: calc(100% - $desktop-wide-grid-gap-width);
      min-width: calc(100% - $desktop-wide-grid-gap-width);
      padding-right: 0;
    }

    &--invisible {
      opacity: 0.2;
    }
  }

  &__item {
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
  .testimonials-row {
    &__row-itself {
      width: calc($desktop-grid-col-width * 4 + $desktop-grid-gap-width * 3 + $desktop-grid-gap-width);
      min-width: calc($desktop-grid-col-width * 4 + $desktop-grid-gap-width * 3 + $desktop-grid-gap-width);
    }

    &__item-wrap {
      padding-right: $desktop-grid-gap-width;

      &:last-child {
        width: calc(100% - $desktop-grid-gap-width);
        min-width: calc(100% - $desktop-grid-gap-width);
      }
    }
  }
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
  .testimonials-row {
    &__row-itself {
      width: calc($laptop-grid-col-width * 4 + $laptop-grid-gap-width * 3 + $laptop-grid-gap-width);
      min-width: calc($laptop-grid-col-width * 4 + $laptop-grid-gap-width * 3 + $laptop-grid-gap-width);
    }

    &__item-wrap {
      padding-right: $laptop-grid-gap-width;

      &:last-child {
        width: calc(100% - $laptop-grid-gap-width);
        min-width: calc(100% - $laptop-grid-gap-width);
      }
    }
  }
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
  .testimonials-row {
    &__row-itself {
      width: calc($tablet-large-grid-col-width * 4 + $tablet-large-grid-gap-width * 3 + $tablet-large-grid-gap-width);
      min-width: calc($tablet-large-grid-col-width * 4 + $tablet-large-grid-gap-width * 3 + $tablet-large-grid-gap-width);
    }

    &__item-wrap {
      padding-right: $tablet-large-grid-gap-width;

      &:last-child {
        width: calc(100% - $tablet-large-grid-gap-width);
        min-width: calc(100% - $tablet-large-grid-gap-width);
      }
    }
  }
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .testimonials-row {
    &__row-itself {
      width: calc($tablet-grid-col-width * 6 + $tablet-grid-gap-width * 5 + $tablet-grid-gap-width);
      min-width: calc($tablet-grid-col-width * 6 + $tablet-grid-gap-width * 5 + $tablet-grid-gap-width);
    }

    &__item-wrap {
      padding-right: $tablet-grid-gap-width;

      &:last-child {
        width: calc(100% - $tablet-grid-gap-width);
        min-width: calc(100% - $tablet-grid-gap-width);
      }
    }
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .testimonials-row {
    &__row-itself {
      width: calc($mobile-grid-col-width * 5 + $mobile-grid-gap-width * 4 + $mobile-grid-gap-width);
      min-width: calc($mobile-grid-col-width * 5 + $mobile-grid-gap-width * 4 + $mobile-grid-gap-width);
    }

    &__item-wrap {
      padding-right: $mobile-grid-gap-width;

      &:last-child {
        width: calc(100% - $mobile-grid-gap-width);
        min-width: calc(100% - $mobile-grid-gap-width);
      }
    }
  }
}

// Logos & testimonials carousel ==============================================
.logos-and-testimonials-carousels {
  width: $desktop-wide-content-width;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;

  &::before {
    content: "";
    width: calc(100% + 300px);
    height: calc(100% + 130px);
    position: absolute;
    inset: -130px auto auto -150px;
    z-index: -1;
    background: rgba(255, 255, 255, 1);
  }

  &__title {
    margin: 127px 0 60px;
    text-align: center;
    font-weight: 600;
  }

  &__logos-carousel {
    width: 100%;
    margin: 0 auto 100px;
  }

  &__divider {
    width: 100%;
    max-width: var(--grid-content-full-width);
    margin: 0 auto 75px;
    border-bottom: 1px rgba(0, 0, 0, 0.1) solid;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__quotes-carousel {
    width: 100%;
    margin: 0 auto 25px;
  }

  &__see-all-link {
    margin-bottom: 70px;
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
  .logos-and-testimonials-carousels {
    width: $desktop-content-width;
  }
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
  .logos-and-testimonials-carousels {
    width: $laptop-content-width;
  }
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
  .logos-and-testimonials-carousels {
    width: $tablet-large-content-width;

    &::before {
      width: calc(100% + 300px);
      height: calc(100% + 80px);
      inset: -80px auto auto -150px;
    }

    &__title {
      margin: 100px 0 50px;
    }

    &__logos-carousel {
      margin: 0 auto 90px;
    }

    &__divider {
      margin: 0 auto 65px;
    }

    &__quotes-carousel {
      margin: 0 auto 25px;
    }
  }
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .logos-and-testimonials-carousels {
    width: $tablet-content-width;

    &::before {
      width: calc(100% + 300px);
      height: calc(100% + 80px);
      inset: -80px auto auto -150px;
    }

    &__title {
      margin: 80px 0 50px;
    }

    &__logos-carousel {
      margin: 0 auto 70px;
    }

    &__divider {
      margin: 0 auto 45px;
    }

    &__quotes-carousel {
      margin: 0 auto 25px;
    }
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .logos-and-testimonials-carousels {
    width: $mobile-content-width;

    &::before {
      width: calc(100% + 300px);
      height: calc(100% + 40px);
      inset: -40px auto auto -150px;
    }

    &__title {
      margin: 57px 0 30px;
    }

    &__logos-carousel {
      margin: 0 auto 50px;
    }

    &__divider {
      margin: 0 auto 35px;
    }

    &__quotes-carousel {
      margin: 0 auto 15px;
    }
  }
}

// Pale teal bg ===============================================================
.pale-teal-bg {
  position: relative;
  z-index: 3;
  background: transparent;

  &::before {
    content: "";
    width: 100%;
    height: calc(100% + 20px);
    position: absolute;
    inset: -20px auto auto 0;
    z-index: -1;
    background: #e6edf0;
    pointer-events: none;
  }
}

.column.promo {
  .promo-wrapper {
    display: flex;
    .icon {
      margin-right: 0.5rem;
    }
    h3.promo-header {
      .content {
        margin-bottom: 1rem;
      }
    }
  }
}

.ui.segment.blank {
  border: none;
  box-shadow: none;
}

.green-section {
  background-color: $brand-color-lighter;
}

.differentiator-text {
  font: 17px/23px sans-serif !important;
}

#featured {
  @media screen and (max-width: 420px) {
    margin-top: 2em;
  }
}

#page-title {
  color: rgba(5, 133, 135, 1);
  font-size: 3.2rem;
  margin-top: 2em;
  @media screen and (max-width: 420px) {
    font-size: 2.5em;
    margin-top: 0em;
  }
}

#page-subtitle {
  font-size: 15pt;
  @media screen and (max-width: 420px) {
    font-size: 12pt;
  }
}

#shrpa-cover-photo {
  @media screen and (max-width: 420px) {
    visibility: hidden;
  }
}

//******************** From RecentCreatorVisits.vue (decide if we should unify or kill that page) *******************************/
@import "@/scss/screen-size-ranges.scss";

//Note: Removed the stats styles

// Foot Cta snippet ===========================================================
.foot-cta-snippet {
  display: flex;
  flex-direction: column;
  align-items: center;

  &__title {
    max-width: 70%;
    margin-bottom: 28px;
    text-align: center;
  }

  &__cta-button {
    outline: none;
  }
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .foot-cta-snippet {
    &__title {
      max-width: 100%;
    }
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .foot-cta-snippet {
    &__title {
      margin-bottom: 15px;
    }
  }
}

// Shrpa round logo ===========================================================
.shrpa-round-logo {
  width: 70px;
  height: 70px;
  border-radius: 100px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #bcc2c5;

  &__img {
    width: 42%;
    position: relative;
    top: -1px;
  }
}

// Book a demo button section with halo =======================================
.book-a-demo-button-section-with-halo {
  height: 1px;
  max-height: 1px;
  padding-bottom: 20px;
  display: flex;
  justify-content: center;
  align-items: flex-end;

  &__button {
    position: relative;
    z-index: 1;
  }

  &__halo {
    width: 550px;
    height: 170px;
    position: absolute;
    inset: auto auto 0 calc(50% - 275px);
    z-index: 0;
    mask-image: linear-gradient(90deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 30%, rgba(0, 0, 0, 1) 70%, rgba(0, 0, 0, 0) 100%);
    background: linear-gradient(0deg, rgba(255, 255, 255, 1) 40%, rgba(255, 255, 255, 0) 100%);
    pointer-events: none;
  }
}

// Recent creator visits ======================================================
.recent-creator-visits {
  &__title-n-stats {
    margin-bottom: 150px;
  }

  &__loader {
    margin-bottom: 150px;
  }

  &__visit-summaries-list-wrap {
    margin-bottom: -100px;
    position: relative;
    z-index: 0;
  }

  &__book-a-demo-button-section-with-halo {
    width: 100%;
    position: sticky;
    inset: auto auto 0 0;
    z-index: 3;
  }

  &__visit-summaries-list {
    width: 1269px;
    padding: 0;
    margin: 0 auto 250px;
    position: relative;
    z-index: 0;
    list-style: none;
  }

  &__summary-section {
    margin-bottom: 80px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__foot-cta-snippets-list {
    margin: 0 auto;
    padding: 95px 0 115px;
    display: flex;
    justify-content: center;
    position: relative;
    background: #e7edf0;

    &::before {
      content: "";
      width: 1px;
      height: calc(100% - 67px - 93px);
      position: absolute;
      inset: 67px auto auto 50%;
      background: rgba(0, 0, 0, 0.15);
    }
  }

  &__foot-cta-snippet {
    width: calc(1269px / 2 - 15px);
    margin-right: 30px;

    &:last-child {
      margin-right: 0;
    }
  }

  &__srpa-round-logo {
    position: absolute;
    inset: auto auto -35px calc(50% - 35px);
  }
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
  .recent-creator-visits {
    &__visit-summaries-list {
      width: 1104px;
    }

    &__foot-cta-snippet {
      width: calc(1104px / 2 - 12px);
      margin-right: 24px;
    }
  }
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
  .recent-creator-visits {
    &__title-n-stats {
      margin-bottom: 100px;
    }

    &__visit-summaries-list {
      width: 762px;
      margin-bottom: 190px;
    }

    &__summary-section {
      margin-bottom: 60px;
    }

    &__foot-cta-snippet {
      width: calc(762px / 2 - 12px) !important;
      margin-right: 25px;
    }
  }
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .recent-creator-visits {
    &__title-n-stats {
      margin-bottom: 60px;
    }

    &__visit-summaries-list {
      width: 502px;
      margin-bottom: 190px;
    }

    &__summary-section {
      margin-bottom: 45px;
    }

    &__foot-cta-snippets-list {
      padding: 60px 0 75px;
      flex-direction: column;
      align-items: center;

      &::before {
        display: none;
      }
    }

    &__foot-cta-snippet {
      width: 502px;
      margin: 0 0 70px;
      position: relative;

      &:last-child {
        margin-bottom: 0;
      }

      &:last-child::before {
        content: "";
        width: 502px;
        height: 1px;
        position: absolute;
        inset: -35px auto auto calc(50% - 251px);
        background: rgba(0, 0, 0, 0.15);
      }
    }
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .recent-creator-visits {
    &__title-n-stats {
      margin-bottom: 60px;
    }

    &__visit-summaries-list {
      width: calc(100% - 36px);
      margin-bottom: 140px;
    }

    &__book-a-demo-button-section-with-halo {
      bottom: 50px;
    }

    &__summary-section {
      margin-bottom: 35px;
    }

    &__foot-cta-snippets-list {
      padding: 55px 0 80px;
      flex-direction: column;
      align-items: center;

      &::before {
        display: none;
      }
    }

    &__foot-cta-snippet {
      width: calc(100% - 36px);
      margin: 0 0 60px;
      position: relative;

      &:last-child {
        margin-bottom: 0;
      }

      &:last-child::before {
        content: "";
        width: 100%;
        height: 1px;
        position: absolute;
        inset: -25px auto auto 0;
        background: rgba(0, 0, 0, 0.15);
      }
    }
  }
}
</style>
